import { Heart, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { type RecipeData, categoryLabels } from '@/data/mockData';

interface FavoritesListProps {
  recipes: RecipeData[];
  favorites: string[];
  onToggleFavorite: (id: string) => void;
  onSelect: (id: string) => void;
}

const FavoritesList = ({ recipes, favorites, onToggleFavorite, onSelect }: FavoritesListProps) => {
  const favRecipes = recipes.filter(r => favorites.includes(r.id));

  if (favRecipes.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
          <Heart className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="text-base font-heading font-semibold mb-1">Sin favoritos aún</h3>
        <p className="text-sm text-muted-foreground max-w-xs mx-auto">
          Toca el corazón en cualquier receta para guardarla aquí y acceder rápido.
        </p>
      </div>
    );
  }

  // Group by category
  const grouped: Record<string, RecipeData[]> = {};
  favRecipes.forEach(r => {
    const cat = categoryLabels[r.category] || r.category;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(r);
  });

  return (
    <div className="space-y-5">
      <p className="text-xs text-muted-foreground">{favRecipes.length} receta{favRecipes.length !== 1 ? 's' : ''} guardada{favRecipes.length !== 1 ? 's' : ''}</p>
      {Object.entries(grouped).map(([catName, recs]) => (
        <div key={catName}>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{catName}</h3>
          <div className="space-y-2">
            {recs.map(r => (
              <div
                key={r.id}
                role="button"
                tabIndex={0}
                onClick={() => onSelect(r.id)}
                onKeyDown={(e) => { if (e.key === 'Enter') onSelect(r.id); }}
                className="w-full flex items-center gap-2.5 bg-card rounded-lg p-3 shadow-card hover:shadow-soft transition-shadow text-left cursor-pointer"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">{r.title}</p>
                  <p className="text-[10px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                    <Clock className="h-2.5 w-2.5" />{r.prepTime}
                    <span>•</span>
                    {r.benefitTag}
                  </p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleFavorite(r.id); }}
                  className="p-0.5 flex-shrink-0"
                >
                  <Heart className="h-3.5 w-3.5 fill-accent text-accent" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FavoritesList;
